import React from 'react';
import { DREAMS_DATABASE, DREAM_CATEGORIES, ARABIC_LETTERS } from '../data/dreamsData';
import { playStoreUrl } from '../lib/appLinks';

interface HomeScreenProps {
  onStart: (initialText?: string) => void;
  onNavigateToPrivacy: () => void;
  onNavigateToDictionary: () => void;
  onNavigateToApp: () => void;
  onSelectDream: (slug: string) => void;
  onSelectLetter: (letter: string) => void;
  onSelectCategory: (categorySlug: string) => void;
}

const preventDefault = (e: React.MouseEvent, handler: () => void) => {
  if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) {
    e.preventDefault();
    handler();
  }
};

const APP_FEATURES: { icon: string; title: string }[] = [
  { icon: '🤖', title: 'تفسير الحلم كاملاً بالـAI' },
  { icon: '🕌', title: 'اختيار المفسر والمنهج عند توفره' },
  { icon: '📖', title: 'بحث في مكتبة الرموز التراثية' },
  { icon: '🎙️', title: 'الإدخال الصوتي للحلم' },
  { icon: '🕰️', title: 'سجل التفسيرات' },
  { icon: '⭐', title: 'المفضلة' }
];

const HomeScreen: React.FC<HomeScreenProps> = ({
  onStart,
  onNavigateToPrivacy,
  onNavigateToDictionary,
  onNavigateToApp,
  onSelectDream,
  onSelectLetter,
  onSelectCategory
}) => {
  return (
    <div className="min-h-screen w-full bg-[#1E293B] text-white relative overflow-hidden flex flex-col items-center justify-start p-4 sm:p-6 text-center" dir="rtl">
      <div className="absolute top-[-10%] right-[-10%] w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-4xl w-full pt-6 sm:pt-10">
        <section className="mb-14">
          <div className="mb-6 inline-flex p-4 bg-emerald-500/10 rounded-3xl border border-emerald-500/20 shadow-2xl">
            <img src="/nav-icon.svg" alt="ابن سيرين - هلال تفسير الأحلام" width="40" height="40" className="w-12 h-12 sm:w-16 sm:h-16 drop-shadow-[0_0_12px_rgba(245,158,11,0.35)]" />
          </div>

          <h1 className="text-3xl sm:text-5xl font-extrabold mb-3 tracking-tight">
            ابن سيرين
          </h1>
          <p className="text-emerald-400 text-lg sm:text-xl font-medium mb-6">
            مفسر الأحلام الذكي وقاموس الرؤى الشامل
          </p>

          <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/60 p-6 sm:p-8 rounded-[2rem] mb-8 shadow-2xl max-w-2xl mx-auto">
            <p className="text-slate-300 leading-relaxed text-base sm:text-lg">
              اكتشف دلالة رموز رؤياك من خلال موسوعة الرموز المستندة إلى أصول تفسير الأحلام لابن سيرين والنابلسي، أو اكتب حلمك كاملاً ليفسّره لك المفسر الذكي.
            </p>
          </div>

          <div className="mb-8 max-w-xl mx-auto overflow-hidden rounded-3xl border border-slate-700/60 shadow-2xl">
            <img src="/banr_ImgTool.webp" alt="تطبيق ابن سيرين لتفسير الأحلام والرؤى" width="960" height="300" className="w-full h-auto object-cover" />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
            <button
              onClick={() => onStart()}
              className="w-full sm:w-auto flex-1 py-4 px-8 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-lg rounded-2xl shadow-xl shadow-emerald-900/30 transition-all active:scale-95"
            >
              تفسير الحلم بالذكاء الاصطناعي الآن
            </button>
            <a
              href={playStoreUrl('home_hero')}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto py-4 px-6 bg-slate-800 hover:bg-slate-700 text-emerald-400 font-bold text-base rounded-2xl border border-slate-700 hover:border-emerald-500/50 transition-all flex items-center justify-center gap-2 no-underline"
            >
              <span>تحميل التطبيق</span>
              <span>📱</span>
            </a>
          </div>
          <p className="text-slate-500 text-xs mt-4 leading-relaxed">
            الفرق ببساطة: الموقع يساعدك على فهم الرمز، والتطبيق يساعدك على فهم الحلم كاملاً.
          </p>
        </section>

        <section className="mb-14">
          <h2 className="text-sm font-bold text-slate-300 mb-3 text-center uppercase tracking-wider">
            فهرس تفسير الأحلام بالحروف الأبجدية
          </h2>
          <div className="flex flex-wrap gap-1.5 justify-center">
            {ARABIC_LETTERS.map((letter) => (
              <a
                key={letter}
                href={`/letter/${encodeURIComponent(letter)}`}
                onClick={(e) => preventDefault(e, () => onSelectLetter(letter))}
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-slate-800/80 hover:bg-emerald-600 border border-slate-700 hover:border-emerald-500 text-white font-bold rounded-lg transition-all text-sm shadow-sm no-underline"
                title={`أحلام بحرف ${letter}`}
              >
                {letter}
              </a>
            ))}
          </div>
          <div className="mt-5">
            <a
              href="/dictionary"
              onClick={(e) => preventDefault(e, onNavigateToDictionary)}
              className="inline-block px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-emerald-400 font-bold rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-all no-underline"
            >
              فتح قاموس الأحلام الكامل ←
            </a>
          </div>
        </section>
        <section className="mb-14 text-right">
          <div className="flex items-center justify-between mb-4 border-b border-slate-700/60 pb-2">
            <h2 className="text-lg sm:text-xl font-bold text-emerald-300">
              أشهر الرؤى وتفسيراتها مستندة إلى مصادر تراثية
            </h2>
            <a
              href="/dictionary"
              onClick={(e) => preventDefault(e, onNavigateToDictionary)}
              className="text-xs text-slate-300 hover:text-emerald-300 font-semibold no-underline"
            >
              عرض الكل ({DREAMS_DATABASE.length}) ←
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DREAMS_DATABASE.slice(0, 6).map((dream) => (
              <a
                key={dream.slug}
                href={`/dream/${dream.slug}`}
                onClick={(e) => preventDefault(e, () => onSelectDream(dream.slug))}
                className="bg-slate-800/50 border border-slate-700/60 hover:border-emerald-500/60 rounded-xl p-4 transition-all hover:shadow-lg text-right flex flex-col justify-between group no-underline block"
              >
                <div>
                  <span className="text-[11px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded font-medium">
                    {dream.category}
                  </span>
                  <h3 className="font-bold text-white text-base mt-2 mb-1 group-hover:text-emerald-400 transition-colors">
                    {dream.name}
                  </h3>
                  <p className="text-xs text-slate-200 line-clamp-2 leading-relaxed">
                    {dream.summary}
                  </p>
                </div>
                <div className="mt-3 pt-2 border-t border-slate-700/40 flex items-center justify-between text-xs">
                  <span className="text-emerald-400 font-semibold">قراءة التفسير ←</span>
                </div>
              </a>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <div className="bg-gradient-to-r from-emerald-900/50 via-slate-800/60 to-teal-900/50 border border-emerald-500/40 rounded-3xl p-8 sm:p-10 text-center max-w-2xl mx-auto shadow-2xl">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
              هل حلمك يحتوي أكثر من رمز واحد؟
            </h2>
            <p className="text-slate-300 text-sm sm:text-base mb-6 max-w-xl mx-auto leading-relaxed">
              القاموس يشرح لك كل رمز على حدة. أما عندما تظهر رموز متعددة في حلم واحد (مثل ثعبان في بيت قديم مع طفل)، فاكتب الحلم كاملاً ليربط المفسر الذكي جميع رموزه بظروفك ويقدّم لك تأويلاً مركّباً شاملاً.
            </p>
            <button
              onClick={() => onStart()}
              className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold rounded-2xl shadow-xl shadow-emerald-900/40 transition-all text-base active:scale-95"
            >
              اكتب حلمي كاملاً للتفسير ←
            </button>
            <p className="text-amber-300/80 text-xs mt-4 leading-relaxed">
              ⚠️ تفسير الذكاء الاصطناعي هو اجتهاد آلي ولا يُنسب مباشرة لابن سيرين أو النابلسي.
            </p>
          </div>
        </section>

        <section className="mb-14 text-right">
          <div className="flex items-center justify-between mb-4 border-b border-slate-700/60 pb-2">
            <h2 className="text-lg sm:text-xl font-bold text-emerald-300 flex items-center gap-2">
              <span>📱</span>
              <span>مميزات تطبيق ابن سيرين للأندرويد</span>
            </h2>
            <a
              href="/app"
              onClick={(e) => preventDefault(e, onNavigateToApp)}
              className="text-xs text-emerald-400 hover:text-emerald-300 font-semibold no-underline"
            >
              صفحة التطبيق ←
            </a>
          </div>
          <p className="text-slate-400 text-sm mb-5 leading-relaxed">
            الحلم الكامل لا يُختصر في رمز واحد: احمل الحلم كاملاً في تطبيقك مع من يفسّره بالذكاء الاصطناعي.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {APP_FEATURES.map((f) => (
              <div key={f.title} className="flex items-center gap-3 p-3.5 bg-slate-800/40 border border-slate-700/50 rounded-xl transition-all hover:bg-slate-800 hover:border-emerald-500">
                <span className="text-2xl shrink-0">{f.icon}</span>
                <span className="text-xs sm:text-sm font-semibold text-white leading-snug">{f.title}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="mb-14 text-right">
          <h2 className="text-lg sm:text-xl font-bold text-emerald-300 mb-4 border-b border-slate-700/60 pb-2">
            تصنيفات الرؤى والأحلام
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {DREAM_CATEGORIES.map((cat) => (
              <a
                key={cat.slug}
                href={`/category/${cat.slug}`}
                onClick={(e) => preventDefault(e, () => onSelectCategory(cat.slug))}
                className="flex items-center gap-3 p-3.5 bg-slate-800/40 border border-slate-700/50 rounded-xl hover:bg-slate-800 hover:border-emerald-500 transition-all text-right no-underline"
              >
                <span className="text-2xl">{cat.icon}</span>
                <span className="text-sm font-semibold text-white">{cat.name}</span>
              </a>
            ))}
          </div>
        </section>

        <section className="mb-8">
          <div className="bg-gradient-to-br from-emerald-900/50 via-slate-900 to-slate-900 border border-emerald-500/40 rounded-3xl p-8 sm:p-10 text-center shadow-2xl">
            <img src="/ibn-sirin.png" alt="" width="72" height="72" className="w-16 h-16 mx-auto mb-4 rounded-2xl" />
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 flex flex-wrap items-center justify-center gap-2">
              فسّر حلمك كاملاً أينما كنت
            </h2>
            <p className="text-slate-300 text-sm sm:text-base mb-7 max-w-xl mx-auto leading-relaxed">
              حمّل تطبيق تفسير الأحلام لابن سيرين والذكاء الاصطناعي مجاناً: التفسير الكامل، اختيار المفسر، البحث في المكتبة، الإدخال الصوتي، السجل والمفضلة.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
              <a
href={playStoreUrl('home_final')}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto flex-1 py-3.5 px-6 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-base rounded-2xl shadow-xl shadow-emerald-900/30 transition-all active:scale-95 no-underline text-center"
              >
                📱 تحميل التطبيق من Google Play
              </a>
              <button
                onClick={() => onStart()}
                className="w-full sm:w-auto py-3.5 px-6 bg-slate-800 hover:bg-slate-700 text-emerald-400 font-bold text-base rounded-2xl border border-slate-700 hover:border-emerald-500/50 transition-all"
              >
                أو جرّب الويب مجاناً
              </button>
            </div>
          </div>
        </section>
        <footer className="mt-8 pt-8 border-t border-slate-700/60 space-y-4">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <a
              href="/dictionary"
              onClick={(e) => preventDefault(e, onNavigateToDictionary)}
              className="text-slate-300 hover:text-emerald-300 transition-colors no-underline"
            >
              قاموس الأحلام
            </a>
            <a
              href="/app"
              onClick={(e) => preventDefault(e, onNavigateToApp)}
              className="text-emerald-400 hover:text-emerald-300 font-semibold no-underline"
            >
              تطبيق الأندرويد 📱
            </a>
            <a
              href={playStoreUrl('home_footer')}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300 font-semibold no-underline"
            >
              تحميل من Google Play
            </a>
            <a
              href="/privacy"
              onClick={(e) => preventDefault(e, onNavigateToPrivacy)}
              className="text-slate-300 hover:text-white transition-colors underline underline-offset-4 no-underline hover:no-underline"
            >
              سياسة الخصوصية
            </a>
          </div>
          <p className="text-slate-300 text-xs font-medium">
            © {new Date().getFullYear()} ابن سيرين لتفسير الأحلام - مرجع محتوى تفسيري مستند إلى التراث
          </p>
        </footer>
      </div>
    </div>
  );
};

export default HomeScreen;